import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import { User, Lock, Mail, Loader2, ArrowRight, CheckCircle2 } from 'lucide-react';

export default function SignUp() {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');

    if (password !== confirmPassword) {
      setErrorMsg('As senhas não coincidem.');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setErrorMsg('A senha deve ter no mínimo 6 caracteres.');
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
        },
      });

      if (error) {
        setErrorMsg(error.message);
      } else {
        setSuccess(true);
      }
    } catch (err: any) {
      setErrorMsg('Ocorreu um erro ao criar conta. Tente novamente.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-[80vh] bg-gradient-to-tr from-primary-50 to-background py-16 px-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-xl border border-primary-100 p-8 sm:p-10 text-center">
          <div className="flex justify-center mb-6 text-primary-500">
            <CheckCircle2 size={64} className="animate-bounce" />
          </div>
          <h2 className="text-3xl font-extrabold text-primary-900 mb-4">Cadastro Realizado!</h2>
          <p className="text-neutral-600 mb-6 leading-relaxed">
            Sua conta para o e-mail <strong className="text-neutral-800">{email}</strong> foi cadastrada com sucesso.
          </p>
          <div className="bg-primary-50 p-5 rounded-2xl text-primary-800 text-sm font-medium mb-8 leading-relaxed">
            Para garantir a segurança, <strong>sua conta precisa ser liberada pelo administrador</strong> antes que você consiga reservar a sala. Por favor, avise o administrador da Clínica Psi Mazulo.
          </div>
          <Link
            to="/cowork/login"
            className="inline-flex items-center gap-2 bg-primary-700 hover:bg-primary-900 text-white font-bold py-3.5 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
          >
            Ir para o Login
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex items-center justify-center min-h-[85vh] bg-gradient-to-tr from-primary-50 to-background py-16 px-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl border border-primary-100 p-8 sm:p-10">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-extrabold text-primary-900 tracking-tight">Criar Conta</h2>
          <p className="text-neutral-500 mt-2 text-sm">Clínica Psi Mazulo<br/>Endereço: A5 Offices, Bloco 1, Sala 252</p>
        </div>

        {errorMsg && (
          <div className="bg-red-50 text-red-700 p-4 rounded-xl text-sm mb-6 border border-red-200">
            {errorMsg}
          </div>
        )}

        <form onSubmit={handleSignUp} className="space-y-5">
          <div>
            <label className="block text-sm font-semibold text-neutral-700 mb-1.5">Nome Completo</label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-neutral-400">
                <User size={18} />
              </span>
              <input
                type="text"
                required
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                placeholder="Dr(a). Nome Sobrenome"
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-500 transition-all text-neutral-800"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-neutral-700 mb-1.5">E-mail</label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-neutral-400">
                <Mail size={18} />
              </span>
              <input
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="seu.email@exemplo.com"
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-500 transition-all text-neutral-800"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-neutral-700 mb-1.5">Senha (mín. 6 dígitos)</label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-neutral-400">
                <Lock size={18} />
              </span>
              <input
                type="password"
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-500 transition-all text-neutral-800"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-neutral-700 mb-1.5">Confirmar Senha</label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-neutral-400">
                <Lock size={18} />
              </span>
              <input
                type="password"
                required
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-500 transition-all text-neutral-800"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center items-center gap-2 bg-primary-700 hover:bg-primary-900 text-white font-bold py-3 px-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 mt-2"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                Cadastrando...
              </>
            ) : (
              <>
                Cadastrar Conta
                <ArrowRight size={18} />
              </>
            )}
          </button>
        </form>

        <div className="mt-6 pt-5 border-t border-neutral-100 text-center">
          <p className="text-sm text-neutral-600">
            Já possui conta?{' '}
            <Link
              to="/cowork/login"
              className="text-primary-700 font-bold hover:text-primary-900 transition-colors"
            >
              Fazer Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
